import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Loader, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LessonPlanBuilder() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    grade: '',
    subject: '',
    duration: '',
    lesson_topics: [{ topic: '', time_per_lesson: 60 }],
    attachment: null,
    attachment_instructions: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/teacher/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const addTopic = () => {
    if (formData.lesson_topics.length < 6) {
      setFormData({
        ...formData,
        lesson_topics: [...formData.lesson_topics, { topic: '', time_per_lesson: 60 }]
      });
    } else {
      toast.error('Maximum 6 lessons allowed');
    }
  };

  const removeTopic = (index) => {
    const newTopics = formData.lesson_topics.filter((_, i) => i !== index);
    setFormData({ ...formData, lesson_topics: newTopics });
  };

  const updateTopic = (index, field, value) => {
    const newTopics = [...formData.lesson_topics];
    newTopics[index][field] = value;
    setFormData({ ...formData, lesson_topics: newTopics });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const filledTopics = formData.lesson_topics.filter(t => t.topic.trim() !== '');
    if (filledTopics.length === 0) {
      toast.error('Please add at least one lesson topic');
      return;
    }

    setGenerating(true);
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('grade', formData.grade);
      submitData.append('subject', formData.subject);
      submitData.append('duration', formData.duration);
      submitData.append('lesson_topics', JSON.stringify(filledTopics));
      
      if (formData.attachment) {
        submitData.append('attachment', formData.attachment);
      }
      if (formData.attachment_instructions) {
        submitData.append('attachment_instructions', formData.attachment_instructions);
      }
      
      const response = await axios.post(`${API_URL}/api/teacher/lesson-plans/generate`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Lesson plan generated successfully!');
      navigate(`/lesson-plans/${response.data.plan_id}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to generate lesson plan';
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sky-50">Loading...</div>;

  if (!settings || settings.selected_grades.length === 0 || settings.selected_subjects.length === 0) {
    return (
      <div className="min-h-screen bg-sky-50" data-testid="lesson-plan-builder-page">
        <header className="glass-header py-4 px-6">
          <div className="max-w-4xl mx-auto">
            <Button onClick={() => navigate('/lesson-plans')} variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Lesson Plans
            </Button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="glass-card p-12 text-center">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Setup Required</h3>
            <p className="text-slate-600 mb-6">Please configure your grades and subjects in Settings before creating lesson plans.</p>
            <Button onClick={() => navigate('/settings')} className="bg-teal-600 hover:bg-teal-700 text-white">
              Go to Settings
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="lesson-plan-builder-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-4xl mx-auto">
          <Button onClick={() => navigate('/lesson-plans')} variant="ghost" data-testid="back-to-lesson-plans">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Lesson Plans
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card p-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Create Lesson Plan</h2>
          <p className="text-slate-600 mb-8">Generate AI-powered, CAPS-aligned lesson plans</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Grade</label>
                <select
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                  required
                  data-testid="grade-select"
                >
                  <option value="">Select Grade</option>
                  {settings.selected_grades.map(grade => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                  required
                  data-testid="subject-select"
                >
                  <option value="">Select Subject</option>
                  {settings.selected_subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Duration</label>
              <Input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                placeholder="e.g., 1 week, 5 days, 2 hours"
                className="bg-white/50 border-slate-200 focus:border-teal-500 rounded-xl"
                required
                data-testid="duration-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-slate-700">Lesson Topics (Max 6)</label>
                <Button
                  type="button"
                  onClick={addTopic}
                  variant="ghost"
                  size="sm"
                  disabled={formData.lesson_topics.length >= 6}
                  data-testid="add-topic-button"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Topic
                </Button>
              </div>
              <div className="space-y-3">
                {formData.lesson_topics.map((lessonData, index) => (
                  <div key={index} className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={lessonData.topic}
                        onChange={(e) => updateTopic(index, 'topic', e.target.value)}
                        placeholder={`Topic ${index + 1}`}
                        className="bg-white/50 border-slate-200 focus:border-teal-500 rounded-xl"
                        data-testid={`topic-input-${index}`}
                      />
                    </div>
                    <div className="w-40">
                      <select
                        value={lessonData.time_per_lesson}
                        onChange={(e) => updateTopic(index, 'time_per_lesson', parseInt(e.target.value))}
                        className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                        data-testid={`time-select-${index}`}
                      >
                        {[...Array(60)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1} min{i + 1 !== 1 ? 's' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {formData.lesson_topics.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeTopic(index)}
                        variant="ghost"
                        size="icon"
                        data-testid={`remove-topic-${index}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Attachment (Optional)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Upload Photo or Document
                  </label>
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={(e) => setFormData({ ...formData, attachment: e.target.files[0] })}
                    className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                    data-testid="attachment-upload"
                  />
                  {formData.attachment && (
                    <p className="text-sm text-slate-600 mt-2">
                      Selected: {formData.attachment.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Instructions for Attachment
                  </label>
                  <textarea
                    value={formData.attachment_instructions}
                    onChange={(e) => setFormData({ ...formData, attachment_instructions: e.target.value })}
                    className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 min-h-24"
                    placeholder="Provide instructions on how to use this attachment in your lessons..."
                    data-testid="attachment-instructions"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6"
              disabled={generating}
              data-testid="generate-lesson-plan-button"
            >
              {generating ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" /> Generating Lesson Plan...
                </>
              ) : (
                'Generate Lesson Plan'
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}